import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Phone,
  RefreshCw,
  Mail,
  MapPin,
  Loader2,
  Trees,
  Calendar,
  Inbox,
  Eye,
  EyeOff,
  ArrowLeft,
  ImageIcon,
  Upload,
  Trash2,
  Plus,
  Brain as BrainIcon,
} from "lucide-react";
import { LeadActions, StatusPicker } from "@/components/LeadActions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "acorn_admin_token";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function telHref(p) {
  return `tel:${(p || "").replace(/[^\d+]/g, "")}`;
}

function LoginCard({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Enter your password");
      return;
    }
    setSubmitting(true);
    try {
      // Validate by calling protected endpoint
      await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${password}` },
        params: { limit: 1 },
      });
      localStorage.setItem(TOKEN_KEY, password);
      toast.success("Welcome back!");
      onSuccess(password);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401) toast.error("Wrong password");
      else if (code === 503) toast.error("Admin access not configured on server");
      else toast.error("Could not log in. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone text-ink flex items-center justify-center px-4 py-10 font-body">
      <div className="w-full max-w-md">
        <a
          href="/"
          data-testid="admin-back-home"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-forest/70 hover:text-safety transition-colors mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Back to site
        </a>
        <div className="card-brutal p-8">
          <div className="flex items-center gap-3 mb-6">
            <img
              src="/acorn-logo.jpg"
              alt="Acorn Tree Service²"
              className="w-16 h-16 object-contain bg-bone border-2 border-forest"
            />
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-safety font-bold">
                Owner Access
              </div>
              <div className="font-display text-3xl tracking-tight uppercase">Acorn Leads<sup className="text-safety text-base ml-0.5">2</sup></div>
            </div>
          </div>

          <form onSubmit={submit}>
            <label className="label-brutal" htmlFor="admin-password">
              Password
            </label>
            <div className="relative">
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type={show ? "text" : "password"}
                className="input-brutal pr-12"
                placeholder="Enter your owner password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus
              />
              <button
                type="button"
                data-testid="admin-password-toggle"
                onClick={() => setShow((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-forest/70 hover:text-safety"
                aria-label={show ? "Hide password" : "Show password"}
              >
                {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={submitting}
              className="btn-primary mt-6 w-full py-4 text-xl flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> CHECKING...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" strokeWidth={3} /> LOG IN
                </>
              )}
            </button>
          </form>

          <p className="mt-6 text-xs text-forest/70 leading-relaxed">
            <strong className="text-forest">Forgot it?</strong> Open{" "}
            <code className="bg-muted2 px-1 py-0.5">/app/backend/.env</code> on your server, find the
            line <code className="bg-muted2 px-1 py-0.5">ADMIN_TOKEN</code>, and that&apos;s your password.
            Change it any time and restart the backend.
          </p>
        </div>
      </div>
    </div>
  );
}

function compressImage(file, maxDim = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function GalleryManager({ token, onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/gallery`, { params: { limit: 60 } });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Could not load gallery");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    try {
      const dataUrl = await compressImage(file);
      setPreview(dataUrl);
    } catch {
      toast.error("Could not read that image");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!preview) {
      toast.error("Pick a photo first");
      return;
    }
    if (!title.trim()) {
      toast.error("Add a short title (e.g. 'Oak removal in Kingston')");
      return;
    }
    setUploading(true);
    try {
      await axios.post(
        `${API}/admin/gallery`,
        { title: title.trim(), caption: caption.trim() || null, image_data: preview },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Photo posted to your site");
      setTitle("");
      setCaption("");
      setPreview(null);
      const fileInput = document.getElementById("gallery-file-input");
      if (fileInput) fileInput.value = "";
      load();
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        onUnauthorized();
      } else {
        toast.error("Upload failed — try a smaller photo");
      }
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this photo? This can't be undone.")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Photo removed");
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
      else toast.error("Could not delete");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="grid lg:grid-cols-12 gap-8 mb-10">
        <form
          onSubmit={onSubmit}
          data-testid="gallery-upload-form"
          className="lg:col-span-5 card-brutal p-6"
        >
          <div className="text-xs uppercase tracking-[0.3em] text-safety font-bold">
            / Add a job photo
          </div>
          <h2 className="font-display text-3xl tracking-tight uppercase mt-2 mb-5">
            Post a New Job
          </h2>

          <label
            htmlFor="gallery-file-input"
            data-testid="gallery-file-label"
            className="block border-2 border-dashed border-forest/60 hover:border-safety transition-colors cursor-pointer p-6 text-center bg-bone"
          >
            {preview ? (
              <img
                src={preview}
                alt="Preview"
                className="w-full max-h-72 object-contain mx-auto"
              />
            ) : (
              <div className="text-forest/70">
                <Upload className="w-8 h-8 mx-auto mb-3 text-safety" strokeWidth={2.5} />
                <div className="font-display text-2xl tracking-tight uppercase">
                  Tap to choose photo
                </div>
                <div className="text-xs uppercase tracking-[0.25em] mt-2">
                  From your phone or computer
                </div>
              </div>
            )}
            <input
              id="gallery-file-input"
              data-testid="gallery-file-input"
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={onFileChange}
            />
          </label>

          <div className="mt-5">
            <label className="label-brutal" htmlFor="gallery-title">Title *</label>
            <input
              id="gallery-title"
              data-testid="gallery-title-input"
              className="input-brutal"
              placeholder="Big oak removal in Kingston"
              maxLength={120}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="mt-4">
            <label className="label-brutal" htmlFor="gallery-caption">
              Caption (optional)
            </label>
            <textarea
              id="gallery-caption"
              data-testid="gallery-caption-input"
              className="input-brutal min-h-[80px] resize-y"
              placeholder="60ft oak leaning over the house — down clean in 4 hours."
              maxLength={400}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />
          </div>

          <button
            type="submit"
            data-testid="gallery-upload-submit"
            disabled={uploading}
            className="btn-primary mt-5 w-full py-4 text-lg flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" /> POSTING...
              </>
            ) : (
              <>
                <Plus className="w-5 h-5" strokeWidth={3} /> POST TO SITE
              </>
            )}
          </button>
        </form>

        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-[0.3em] text-safety font-bold">
            / Posted photos
          </div>
          <h2 className="font-display text-3xl tracking-tight uppercase mt-2 mb-5">
            Your Gallery
            {items.length > 0 && (
              <span className="text-forest/40 ml-3">({items.length})</span>
            )}
          </h2>

          {loading ? (
            <div className="card-brutal p-10 flex flex-col items-center text-forest/70">
              <Loader2 className="w-7 h-7 animate-spin text-safety mb-2" />
              <div className="text-sm uppercase tracking-[0.25em]">Loading...</div>
            </div>
          ) : items.length === 0 ? (
            <div className="card-brutal p-10 text-center">
              <ImageIcon className="w-10 h-10 text-forest/40 mx-auto mb-4" strokeWidth={2} />
              <div className="font-display text-2xl tracking-tight uppercase mb-2">
                No photos yet
              </div>
              <div className="text-forest/70 text-sm">
                Upload your first job photo on the left — it&apos;ll instantly show on your site.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {items.map((it) => (
                <div
                  key={it.id}
                  data-testid={`gallery-item-${it.id}`}
                  className="border-2 border-forest bg-white relative group"
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={it.image_data}
                      alt={it.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-2.5 border-t-2 border-forest">
                    <div className="font-display text-sm tracking-tight uppercase line-clamp-2">
                      {it.title}
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid={`gallery-delete-${it.id}`}
                    onClick={() => onDelete(it.id)}
                    disabled={deletingId === it.id}
                    className="absolute top-2 right-2 w-9 h-9 bg-safety border-2 border-forest text-bone flex items-center justify-center hover:bg-bone hover:text-safety transition-colors disabled:opacity-60"
                    aria-label="Delete photo"
                  >
                    {deletingId === it.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("leads");
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchQuotes = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) setLoading(true);
      else setRefreshing(true);
      try {
        const { data } = await axios.get(`${API}/quotes`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 100 },
        });
        setQuotes(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err?.response?.status === 401) {
          toast.error("Session expired — please log in again");
          onLogout();
        } else {
          toast.error("Could not load leads");
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [token, onLogout],
  );

  useEffect(() => {
    fetchQuotes(true);
  }, [fetchQuotes]);

  const today = quotes.filter((q) => {
    if (!q.created_at) return false;
    const d = new Date(q.created_at);
    const now = new Date();
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  }).length;

  const week = quotes.filter((q) => {
    if (!q.created_at) return false;
    const d = new Date(q.created_at);
    return Date.now() - d.getTime() < 7 * 24 * 60 * 60 * 1000;
  }).length;

  return (
    <div className="min-h-screen bg-bone text-ink font-body">
      {/* Top bar */}
      <header className="bg-forest text-bone border-b-2 border-forest sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between">
          <a href="/" data-testid="admin-brand" className="flex items-center gap-3">
            <img
              src="/acorn-logo.jpg"
              alt="Acorn Tree Service²"
              className="h-10 w-auto bg-bone border-2 border-bone"
            />
            <div className="leading-none">
              <div className="font-display text-2xl tracking-wider">ACORN — LEADS<sup className="text-safety text-sm ml-0.5">2</sup></div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-bone/70">
                Owner dashboard
              </div>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={() => fetchQuotes(false)}
              disabled={refreshing}
              className="btn-dark px-4 py-2 text-base flex items-center gap-2"
              style={{ background: "#1B2A22", color: "#F4F1ED" }}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} strokeWidth={3} />
              <span className="hidden sm:inline">REFRESH</span>
            </button>
            <a
              data-testid="admin-brain-link"
              href="/brain"
              className="btn-dark px-4 py-2 text-base flex items-center gap-2"
              style={{ background: "#E65C00", color: "#F4F1ED" }}
              title="Open AI Brain"
            >
              <BrainIcon className="w-4 h-4" strokeWidth={2.5} />
              <span className="hidden sm:inline">BRAIN</span>
            </a>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="btn-primary px-4 py-2 text-base flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" strokeWidth={3} />
              <span className="hidden sm:inline">LOG OUT</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        {/* Tabs */}
        <div className="flex border-b-2 border-forest mb-8 -mt-2">
          <button
            data-testid="tab-leads"
            onClick={() => setTab("leads")}
            className={`px-5 sm:px-7 py-3 font-display tracking-widest text-lg sm:text-xl border-2 border-forest border-b-0 -mb-[2px] flex items-center gap-2 ${
              tab === "leads"
                ? "bg-safety text-white"
                : "bg-bone text-forest hover:bg-muted2"
            }`}
            type="button"
          >
            <Inbox className="w-4 h-4" strokeWidth={3} /> LEADS
          </button>
          <button
            data-testid="tab-gallery"
            onClick={() => setTab("gallery")}
            className={`px-5 sm:px-7 py-3 font-display tracking-widest text-lg sm:text-xl border-2 border-forest border-b-0 border-l-0 -mb-[2px] flex items-center gap-2 ${
              tab === "gallery"
                ? "bg-safety text-white"
                : "bg-bone text-forest hover:bg-muted2"
            }`}
            type="button"
          >
            <ImageIcon className="w-4 h-4" strokeWidth={3} /> GALLERY
          </button>
        </div>

        {tab === "gallery" ? (
          <GalleryManager token={token} onUnauthorized={onLogout} />
        ) : (
          <>
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {[
            { label: "Total Leads", value: quotes.length, icon: Inbox },
            { label: "This Week", value: week, icon: Calendar },
            { label: "Today", value: today, icon: Calendar },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="card-brutal p-6">
              <div className="flex items-center gap-3 text-forest/70">
                <Icon className="w-5 h-5 text-safety" strokeWidth={2.5} />
                <span className="text-xs uppercase tracking-[0.25em] font-bold">{label}</span>
              </div>
              <div
                data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-display text-6xl tracking-tight mt-2"
              >
                {loading ? "—" : value}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-safety font-bold">
              / Incoming requests
            </div>
            <h1 className="font-display uppercase text-4xl sm:text-5xl tracking-tight leading-[0.9] mt-2">
              Customer Quote Requests
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="card-brutal p-12 flex flex-col items-center justify-center text-forest/70">
            <Loader2 className="w-8 h-8 animate-spin text-safety mb-3" />
            <div className="text-sm uppercase tracking-[0.25em]">Loading leads...</div>
          </div>
        ) : quotes.length === 0 ? (
          <div className="card-brutal p-12 text-center">
            <Inbox className="w-10 h-10 text-forest/40 mx-auto mb-4" strokeWidth={2} />
            <div className="font-display text-3xl tracking-tight uppercase mb-2">No leads yet</div>
            <div className="text-forest/70">
              When someone fills out the quote form, you&apos;ll see them here.
            </div>
          </div>
        ) : (
          <div className="grid gap-5">
            {quotes.map((q) => (
              <article
                key={q.id}
                data-testid={`lead-card-${q.id}`}
                className="card-brutal p-6"
              >
                <div className="grid lg:grid-cols-12 gap-5">
                  <div className="lg:col-span-5">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <StatusPicker lead={q} token={token} onChanged={() => fetchQuotes(false)} />
                      <span className="bg-bone text-forest px-2 py-1 border-2 border-forest text-[10px] uppercase tracking-[0.2em] font-bold">
                        {q.service_type || "Quote"}
                      </span>
                      {q.invoices?.length > 0 && (
                        <span className="bg-forest text-bone px-2 py-1 border-2 border-forest text-[10px] uppercase tracking-[0.2em] font-bold">
                          {q.invoices.length} doc{q.invoices.length > 1 ? "s" : ""} sent
                        </span>
                      )}
                    </div>
                    <h2 className="font-display text-3xl tracking-tight uppercase">{q.name}</h2>
                    <div className="text-xs uppercase tracking-[0.2em] text-forest/60 mt-1">
                      {formatDate(q.created_at)}
                    </div>
                  </div>

                  <div className="lg:col-span-4 space-y-2 text-sm">
                    <a
                      href={telHref(q.phone)}
                      data-testid={`lead-call-${q.id}`}
                      className="flex items-center gap-2 text-forest hover:text-safety transition-colors font-display text-xl tracking-tight"
                    >
                      <Phone className="w-4 h-4" strokeWidth={3} /> {q.phone}
                    </a>
                    {q.email && (
                      <a
                        href={`mailto:${q.email}`}
                        className="flex items-center gap-2 text-forest/80 hover:text-safety break-all"
                      >
                        <Mail className="w-4 h-4 flex-shrink-0" /> {q.email}
                      </a>
                    )}
                    <div className="flex items-start gap-2 text-forest/80">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /> {q.address}
                    </div>
                  </div>

                  <div className="lg:col-span-3 flex flex-col gap-2">
                    <a
                      href={telHref(q.phone)}
                      data-testid={`lead-callback-${q.id}`}
                      className="btn-primary px-4 py-3 text-base flex items-center justify-center gap-2"
                    >
                      <Phone className="w-4 h-4" strokeWidth={3} /> CALL BACK
                    </a>
                    <a
                      href={`sms:${(q.phone || "").replace(/[^\d+]/g, "")}?&body=${encodeURIComponent(
                        `Hi ${q.name}, this is Acorn Tree Service following up on your quote request. When's a good time to come look at it?`,
                      )}`}
                      data-testid={`lead-text-${q.id}`}
                      className="btn-dark px-4 py-3 text-base flex items-center justify-center gap-2"
                      style={{ background: "#1B2A22", color: "#F4F1ED" }}
                    >
                      TEXT
                    </a>
                    <LeadActions lead={q} token={token} onChanged={() => fetchQuotes(false)} />
                  </div>
                </div>

                {q.description && (
                  <div className="mt-5 pt-5 border-t-2 border-forest/15">
                    <div className="text-xs uppercase tracking-[0.25em] text-forest/60 font-bold mb-2">
                      Notes from customer
                    </div>
                    <p className="text-forest/90 whitespace-pre-line leading-relaxed">
                      {q.description}
                    </p>
                  </div>
                )}

                {q.invoices?.length > 0 && (
                  <div className="mt-5 pt-5 border-t-2 border-forest/15">
                    <div className="text-xs uppercase tracking-[0.25em] text-forest/60 font-bold mb-2">
                      Sent Documents
                    </div>
                    <ul className="space-y-1 text-sm">
                      {q.invoices.map((inv) => (
                        <li key={inv.id} className="flex items-center justify-between border border-forest/20 px-3 py-2 bg-bone">
                          <span className="font-bold">
                            {(inv.kind || "invoice").toUpperCase()} {inv.doc_number}
                          </span>
                          <span>${(inv.total || 0).toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) setToken(stored);
    setHydrated(true);
  }, []);

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    toast.message("Logged out");
  };

  if (!hydrated) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-safety" />
      </div>
    );
  }

  if (!token) return <LoginCard onSuccess={setToken} />;
  return <Dashboard token={token} onLogout={onLogout} />;
}
